// i18n.js — every string the RPG can emit, in vi and en. Events carry BOTH
// languages (`text: {vi,en}`) so a saved fight re-reads correctly after the story
// language changes; the HUD and the snapshot pick one at render time.
// No DOM here: hud.js and vr-menu.js are the only files allowed to touch it.

export const LANGS = ['vi', 'en']

// A label is a bare string or {vi,en}. Preferred language, then the other, then anything.
export function pickLabel(labelOrString, lang = 'vi') {
  if (typeof labelOrString === 'string') return labelOrString
  if (!labelOrString || typeof labelOrString !== 'object') return ''
  const order = [lang, ...LANGS.filter(l => l !== lang)]
  for (const key of order) if (typeof labelOrString[key] === 'string' && labelOrString[key]) return labelOrString[key]
  const first = Object.values(labelOrString).find(v => typeof v === 'string' && v)
  return first || ''
}

export function bothLabels(labelOrString) {
  return { vi: pickLabel(labelOrString, 'vi'), en: pickLabel(labelOrString, 'en') }
}

const STAT = { hp: { vi: 'máu', en: 'hp' }, mp: { vi: 'mana', en: 'mana' } }

// One template per event type. `p` is the already-resolved parts bag from rules.js —
// names and skill labels come in as {vi,en}, numbers as numbers.
const EVENTS = {
  cast: p => ({
    vi: p.target && p.target.vi !== p.actor.vi ? `${p.actor.vi} dùng ${p.skill.vi} lên ${p.target.vi}` : `${p.actor.vi} dùng ${p.skill.vi}`,
    en: p.target && p.target.en !== p.actor.en ? `${p.actor.en} uses ${p.skill.en} on ${p.target.en}` : `${p.actor.en} uses ${p.skill.en}`,
  }),
  hit: p => ({
    vi: `${p.actor.vi} dùng ${p.skill.vi} lên ${p.target.vi}: -${p.delta} máu (${p.hp}/${p.hpMax})`,
    en: `${p.actor.en} hits ${p.target.en} with ${p.skill.en}: -${p.delta} hp (${p.hp}/${p.hpMax})`,
  }),
  absorb: p => ({
    vi: `Khiên của ${p.target.vi} chặn ${p.delta} sát thương (còn ${p.shield})`,
    en: `${p.target.en}'s ward soaks ${p.delta} damage (${p.shield} left)`,
  }),
  shield: p => ({
    vi: `${p.actor.vi} dựng ${p.skill.vi}: khiên ${p.delta} trong ${p.rounds} hiệp`,
    en: `${p.actor.en} raises ${p.skill.en}: ${p.delta} shield for ${p.rounds} rounds`,
  }),
  heal: p => ({
    vi: `${p.actor.vi} hồi ${p.delta} máu cho ${p.target.vi} (${p.hp}/${p.hpMax})`,
    en: `${p.actor.en} heals ${p.target.en} for ${p.delta} (${p.hp}/${p.hpMax})`,
  }),
  drain: p => ({
    vi: `${p.actor.vi} hút ${p.delta} ${STAT[p.stat].vi} của ${p.target.vi}${p.permanent ? ' (vĩnh viễn)' : ''} (${p.cur}/${p.max})`,
    en: `${p.actor.en} siphons ${p.delta} ${STAT[p.stat].en} from ${p.target.en}${p.permanent ? ' (permanent)' : ''} (${p.cur}/${p.max})`,
  }),
  defeated: p => ({ vi: `${p.target.vi} gục ngã.`, en: `${p.target.en} is defeated.` }),
  recovered: p => ({ vi: `${p.actor.vi} gượng dậy (${p.hp}/${p.hpMax}).`, en: `${p.actor.en} comes to (${p.hp}/${p.hpMax}).` }),
  cultivated: p => ({
    vi: p.tier === 'dual'
      ? `${p.actor.vi} song tu cùng ${p.target.vi} ${p.years} năm: +${p.delta} (${p.age} tuổi, còn ${p.lifespan} năm thọ)`
      : `${p.actor.vi} tu luyện ${p.years} năm: +${p.delta} (${p.age} tuổi, còn ${p.lifespan} năm thọ)`,
    en: p.tier === 'dual'
      ? `${p.actor.en} dual-cultivates with ${p.target.en} for ${p.years} year(s): +${p.delta} (age ${p.age}, ${p.lifespan} years left)`
      : `${p.actor.en} cultivates for ${p.years} year(s): +${p.delta} (age ${p.age}, ${p.lifespan} years left)`,
  }),
  breakthrough: p => ({
    vi: `Đột phá! ${p.actor.vi} vào ${p.realm.vi} (${p.hpMax} máu / ${p.mpMax} mana)`,
    en: `Breakthrough! ${p.actor.en} enters ${p.realm.en} (${p.hpMax} hp / ${p.mpMax} mana)`,
  }),
  learned: p => ({ vi: `${p.target.vi} học được ${p.skill.vi} từ ${p.actor.vi}.`, en: `${p.target.en} learns ${p.skill.en} from ${p.actor.en}.` }),
  'level-up': p => ({ vi: `${p.actor.vi}: ${p.skill.vi} lên cấp ${p.level}.`, en: `${p.actor.en}: ${p.skill.en} reaches level ${p.level}.` }),
  'encounter-start': p => ({ vi: `Giao đấu: ${p.names.vi}.`, en: `Fight on: ${p.names.en}.` }),
  'encounter-end': () => ({ vi: 'Giao đấu kết thúc.', en: 'The fight is over.' }),
  'lifespan-end': p => ({ vi: `${p.actor.vi} đã cạn thọ nguyên.`, en: `${p.actor.en}'s lifespan has run out.` }),
  'story-score': p => ({ vi: `${p.key}: ${p.delta > 0 ? '+' : ''}${p.delta}`, en: `${p.key}: ${p.delta > 0 ? '+' : ''}${p.delta}` }),
  illegal: p => ({ vi: `Không hợp lệ: ${REASONS[p.reason]?.vi || p.reason}.`, en: `Illegal: ${REASONS[p.reason]?.en || p.reason}.` }),
}

export function eventText(type, parts = {}) {
  const build = EVENTS[type]
  if (!build) return { vi: '', en: '' }
  try { return build(parts) } catch { return { vi: '', en: '' } } // a half-filled parts bag must never break a cast
}

// Refusal codes. canCast/teach/cultivate return the CODE; the HUD turns it into a tooltip.
export const REASONS = {
  'not-your-turn': { vi: 'chờ đến lượt bạn', en: 'wait for your turn' },
  'stale-turn': { vi: 'lượt đã thay đổi', en: 'that turn has already ended' },
  'start-fight': { vi: 'chọn thử thách để bắt đầu', en: 'start a challenge first' },
  focus: { vi: 'cần 3 tụ lực · xuất chưởng hoặc hộ thể', en: 'needs 3 focus · Strike or Guard to build it' },
  'training-required': { vi: 'hoàn thành trận pháp tu luyện', en: 'complete the cultivation matrix' },
  'training-busy': { vi: 'đang tu luyện', en: 'finish or cancel training first' },
  'stale-training': { vi: 'buổi tu luyện đã kết thúc', en: 'that training session has ended' },
  'training-locked': { vi: 'tăng cấp tu luyện hoặc thắng thử thách để mở', en: 'improve cultivation mastery or win a trial to unlock' },
  'trial-locked': { vi: 'thắng thử thách trước để mở', en: 'win the previous trial to unlock' },
  'unknown-skill': { vi: 'không có kỹ năng này', en: 'no such skill' },
  'not-known': { vi: 'chưa học kỹ năng này', en: 'skill not learned' },
  absent: { vi: 'không có mặt', en: 'not present' },
  defeated: { vi: 'đang gục ngã', en: 'is defeated' },
  mana: { vi: 'không đủ mana', en: 'not enough mana' },
  cooldown: { vi: 'kỹ năng còn hồi', en: 'still cooling down' },
  'wrong-mode': { vi: 'không dùng được lúc này', en: 'not usable right now' },
  'no-target': { vi: 'chưa chọn mục tiêu', en: 'no target' },
  'bad-target': { vi: 'mục tiêu không hợp lệ', en: 'invalid target' },
  'not-in-fight': { vi: 'không ở trong trận này', en: 'not in this fight' },
  'in-combat': { vi: 'đang giao đấu — dừng đấu đã', en: 'end the duel first' },
  'out-of-range': { vi: 'ngoài tầm', en: 'out of range' },
  spot: { vi: 'phải tới đúng vị trí', en: 'wrong place' },
  partner: { vi: 'cần bạn tu ở bên', en: 'needs a partner here' },
  realm: { vi: 'chưa đủ cảnh giới', en: 'realm too low' },
  trust: { vi: 'chưa đủ tin tưởng', en: 'not trusted enough' },
  teacher: { vi: 'người này không dạy kỹ năng đó', en: 'they do not teach that' },
  'already-known': { vi: 'đã biết kỹ năng này', en: 'already known' },
  'max-realm': { vi: 'đã tới đỉnh', en: 'already at the cap' },
  disabled: { vi: 'hệ thống đang tắt', en: 'the system is off' },
  'bad-command': { vi: 'lệnh không hợp lệ', en: 'bad command' },
}

export function reasonText(code, lang = 'vi') { return pickLabel(REASONS[code] || code, lang) }

// HUD chrome. hud.js/vr-menu.js read this; nothing here knows about the DOM.
export const HUD_TEXT = {
  vi: {
    title: 'Hệ thống', hp: 'Máu', mp: 'Mana', shield: 'Khiên', realm: 'Cảnh giới', body: 'Thể chất',
    age: 'Tuổi', lifespan: 'Thọ nguyên', round: 'Hiệp', year: 'Năm', level: 'Cấp', you: 'Bạn',
    log: 'Nhật ký', ready: 'Sẵn sàng', cooling: 'Đang hồi', defeated: 'Gục ngã', target: 'Mục tiêu',
    collapse: 'Thu gọn bảng', expand: 'Mở bảng',
    rite: 'Trận pháp', riteActions: 'bước', riteCombo: 'Liên kích', riteCancel: 'Huỷ (Esc)',
    cultivate: 'Tu luyện', dual: 'Song tu', learn: 'Học kỹ năng', duel: 'Đấu', stop: 'Dừng đấu',
    encounter: 'Giao đấu', noEncounter: 'Đang rảnh',
  },
  en: {
    title: 'System', hp: 'HP', mp: 'Mana', shield: 'Ward', realm: 'Realm', body: 'Body',
    age: 'Age', lifespan: 'Lifespan', round: 'Round', year: 'Year', level: 'Lv', you: 'You',
    log: 'Log', ready: 'Ready', cooling: 'Cooling', defeated: 'Defeated', target: 'Target',
    collapse: 'Collapse the panel', expand: 'Expand the panel',
    rite: 'Array', riteActions: 'actions', riteCombo: 'Combo', riteCancel: 'Cancel (Esc)',
    cultivate: 'Cultivate', dual: 'Dual cultivate', learn: 'Learn a skill', duel: 'Fight', stop: 'Stop',
    encounter: 'Fight', noEncounter: 'Free time',
  },
}

export function hudText(lang = 'vi') { return HUD_TEXT[lang] || HUD_TEXT.vi }

// "R4 · Trần Bình An dùng Thuần Dương Chưởng lên Điền Bất Khuyết: -104 máu (1096/1200)"
export function recentLine(entry, lang = 'vi') {
  const body = pickLabel(entry?.text, lang)
  return body ? `R${entry.round ?? 0} · ${body}` : ''
}

export function resolveLang(...candidates) {
  for (const c of candidates) if (typeof c === 'string' && LANGS.includes(c.slice(0, 2).toLowerCase())) return c.slice(0, 2).toLowerCase()
  return 'vi'
}
